// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
    CaseTemplateOutput,
    CaseInput,
    PAPValueArray,
    SeverityValueArray,
    TLPValueArray,
    InputShare,
    OrganisationOutput,
    ProfileOutput,
    PageTemplateInput,
    PageTemplateOutput,
} from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd';
import { SecurityInput } from '@Atoms/Inputs/SecurityInput/SecurityInput';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { THForm } from '@Molecules/THForm/THForm';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { normalizeDate } from '@Helpers/date';
import { getAvailableLinks } from '@Helpers/caseSharing';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector, computeListSelector } from '@Organisms/Organisation/selector';
import { useCurrentUser } from '@Queries/userQueries';
import useListQuery from '@Hooks/useListQuery';
import { computeListSelector as computeProfileListSelector } from '@Organisms/Profile/selector';
import { CaseCreateFormTabs } from './CaseCreateFormTabs';

import './CaseCreateForm.scss';

export interface CaseCreateFormValues {
    title: CaseInput['title'];
    description: CaseInput['description'];
    startDate: CaseInput['startDate'];
    severity: CaseInput['severity'];
    tlp: CaseInput['tlp'];
    pap: CaseInput['pap'];
    tasks: CaseInput['tasks'];
    customFields: CaseInput['customFields'];
    tags: CaseInput['tags'];
    caseTemplate: CaseInput['caseTemplate'];
    taskRule?: CaseInput['taskRule'];
    observableRule?: CaseInput['observableRule'];
    sharingParameters?: (InputShare & { editable?: boolean; isShared?: boolean })[];
    pageTemplates?: PageTemplateInput[];
    pages?: PageTemplateInput[];
}

export interface CaseSharingProfile {
    name: string;
    description: string;
    autoShare: boolean;
    editable: boolean;
    permissionProfile: string;
    taskRule: string;
    observableRule: string;
}

interface CaseCreateFormProps {
    onFinish: (values: CaseCreateFormValues) => Promise<void>;
    caseTemplate?: CaseTemplateOutput;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    onCancel: () => void;
    initialValues?: Partial<CaseInput>;
}

function sortByOrder(a: { order: number }, b: { order: number }) {
    return a.order - b.order;
}

function editOrder(item: any, index: number) {
    return { ...item, order: index };
}

export const CaseCreateForm = ({
    onFinish,
    caseTemplate,
    form,
    isValid,
    onChange,
    onCancel,
    initialValues,
}: CaseCreateFormProps): ReactElement => {
    const i = useLocalMessage();

    const { data: currentUser } = useCurrentUser();
    const [stateLoading, setStateLoading] = useState(false);

    const {
        titlePrefix = '',
        severity = 2,
        tlp = 2,
        pap = 2,
        description = '',
        tasks = [],
        tags = [],
        name = '',
        customFields = [],
        extraData = {},
    } = caseTemplate || {};

    const { data: pageTemplateList = [], refetch: getPageTemplates } = useListQuery({
        controller: client.PageTemplateController,
        queryParams: {
            selector: { head: [{ name: 'listPageTemplate' }], name: 'get-all-page-templates' },
        },
    });

    const onCreateFormFinish = (values: CaseCreateFormValues): void => {
        setStateLoading(true);
        getPageTemplates();
        const updatedValues = {
            ...values,
        };

        if (caseTemplate) {
            updatedValues.title = `${titlePrefix} ${values.title}`;
            updatedValues.caseTemplate = name;
        }

        if (updatedValues?.tasks) {
            updatedValues.tasks = updatedValues.tasks.map(editOrder);
        }

        if (updatedValues?.customFields) {
            updatedValues.customFields = updatedValues.customFields.map(editOrder);
        }

        if (values?.pageTemplates) {
            updatedValues.pages = values.pageTemplates.reduce((pages: PageTemplateInput[], page) => {
                const formattedItem = pageTemplateList.find(
                    (element) => (page as PageTemplateOutput)._id === element._id || page.title === element.title,
                );

                if (formattedItem) {
                    pages.push(formattedItem);
                } else {
                    pages.push(page);
                }
                return pages;
            }, []);
            delete updatedValues.pageTemplates;
        }

        onFinish(updatedValues).finally(() => {
            setStateLoading(false);
        });
    };

    const { data: sharingProfileList = [] } = useGenericQuery({
        fetchCallback: () => client.OrganisationController.getSharingProfile(),
        key: ['get-organisation-sharing-profile'],
        manual: true,
        fetchOnStart: false,
    });

    const { data: currentOrg } = useOneQuery<OrganisationOutput>({
        controller: client.OrganisationController,
        selector: computeGetSelector({ orgId: currentUser?.organisation || '' }),
        manual: true,
        fetchOnStart: true,
    });

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: profileList = [] } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: computeProfileListSelector(),
        },
        controller: client.ProfileController,
        manual: true,
        fetchOnStart: true,
    });

    const orgProfileList = useMemo(() => profileList.filter((profile) => profile.forOrg), [profileList]);

    const availableLinks = useMemo(
        () => currentOrg?.links && getAvailableLinks(currentOrg.links, orgList),
        [currentOrg?.links, orgList],
    );

    const sharingParameters = useMemo(() => {
        return (
            availableLinks?.map(({ toOrganisation = '', linkType = '' }) => {
                const sharingProfile = (sharingProfileList as CaseSharingProfile[]).find(({ name }) => {
                    return name === linkType;
                });
                return {
                    organisation: toOrganisation,
                    profile: sharingProfile?.permissionProfile,
                    isShared: sharingProfile?.autoShare,
                    share: sharingProfile?.autoShare,
                    observableRule: sharingProfile?.observableRule,
                    taskRule: sharingProfile?.taskRule,
                    editable: sharingProfile?.editable,
                };
            }) || []
        );
    }, [availableLinks, sharingProfileList]);

    const defaultCaseValues: Partial<CaseCreateFormValues> = {
        severity: initialValues?.severity !== undefined ? initialValues?.severity : severity,
        tlp: initialValues?.tlp !== undefined ? initialValues?.tlp : tlp,
        pap: initialValues?.pap !== undefined ? initialValues?.pap : pap,
        title: initialValues?.title !== undefined ? initialValues?.title : undefined,
        description: initialValues?.description || description,
        tasks: tasks.sort(sortByOrder),
        tags: [...(initialValues?.tags || []), ...tags],
        customFields: [...(initialValues?.customFields || []), ...customFields].sort(sortByOrder),
        startDate: initialValues?.startDate !== undefined ? initialValues?.startDate : (moment() as any),
        taskRule: currentOrg?.taskRule,
        observableRule: currentOrg?.observableRule,
        pageTemplates: extraData.pageTemplateHeaders,
        sharingParameters: sharingParameters,
    };

    useEffect(() => {
        form.resetFields();
    }, [caseTemplate, form, sharingParameters]);

    return (
        <THForm<CaseCreateFormValues>
            onFinish={(values) => onCreateFormFinish({ ...(defaultCaseValues || {}), ...values })}
            initialValues={defaultCaseValues || {}}
            form={form}
            onValuesChange={onChange}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        loading={stateLoading}
                        onCancel={onCancel}
                        disabled={!isValid}
                        footerText={i('general.confirm')}
                    />
                }
            >
                <THForm.Item
                    label={i('case.create.form.title.label')}
                    name="title"
                    rules={[{ type: 'string', required: true, message: i('case.create.form.title.required') }]}
                >
                    <THInput
                        placeholder={i('case.create.form.title.placeholder')}
                        addonBefore={titlePrefix || undefined}
                        autoFocus
                    />
                </THForm.Item>

                <THForm.Item
                    label={i('case.create.form.date.label')}
                    name="startDate"
                    rules={[{ required: true, message: i('case.create.form.date.required') }]}
                    normalize={normalizeDate}
                >
                    <THDatePicker />
                </THForm.Item>

                <div className="case-create-form__section">
                    <THForm.Item name="severity" label={i('case.create.form.severity.label')}>
                        <SecurityInput type="severity" hasLabel={false} valueArray={SeverityValueArray} />
                    </THForm.Item>

                    <THForm.Item name="tlp" label={i('case.create.form.tlp.label')}>
                        <SecurityInput type="tlp" valueArray={TLPValueArray} />
                    </THForm.Item>

                    <THForm.Item name="pap" label={i('case.create.form.pap.label')}>
                        <SecurityInput type="pap" valueArray={PAPValueArray} />
                    </THForm.Item>
                </div>

                <THForm.Item<CaseCreateFormValues>
                    noStyle
                    shouldUpdate={(prevValues, newValues) => prevValues.description !== newValues.description}
                >
                    <THForm.Item label={i('case.create.form.tags.label')} name="tags">
                        <THTagSelector.Autocomplete placeholder={i('common.tags')} />
                    </THForm.Item>
                </THForm.Item>

                <THForm.Item<CaseCreateFormValues>
                    noStyle
                    shouldUpdate={(prevValues, newValues) => prevValues.tags !== newValues.tags}
                >
                    <THForm.Item
                        label={i('case.create.form.description.label')}
                        name="description"
                        rules={[
                            {
                                required: true,
                                message: i('observable.create.form.value.required'),
                            },
                        ]}
                    >
                        <THMarkdown sameSize />
                    </THForm.Item>
                </THForm.Item>

                <CaseCreateFormTabs
                    form={form}
                    onChange={onChange}
                    orgList={orgList}
                    orgProfileList={orgProfileList}
                    currentOrg={currentOrg}
                    pageTemplates={extraData.pageTemplateHeaders || []}
                />
            </THForm.Layout>
        </THForm>
    );
};
